import React, { useState, useRef } from 'react';
import { useStore } from '../store/useStore';
import { 
    BuildingOfficeIcon, 
    BriefcaseIcon, 
    DocumentTextIcon, 
    CheckIcon, 
    UploadIcon, 
    CameraIcon, 
    SparklesIcon 
} from './icons';
import { fileToBase64Compressed } from '../utils/imageCompression';
import { updateFirestoreSettings, addFirestoreTemplate } from '../api/firestore';
import type { FuneralHomeInfo, IndividualService, ServicePlan, DocumentTemplate } from '../types';

export const OnboardingWizard: React.FC = () => {
    const { currentUser, settings, updateOnboardingStep, fetchAllData, saveService, savePlan } = useStore();
    
    // Fallback in case not loaded
    const currentStepIndex = !currentUser?.onboarding?.steps.profile ? 1 
        : !currentUser?.onboarding?.steps.catalog ? 2 
        : !currentUser?.onboarding?.steps.templates ? 3 : 4;
        
    const [step, setStep] = useState(currentStepIndex);
    const [isProcessing, setIsProcessing] = useState(false);

    // Formulario de perfil
    const [formData, setFormData] = useState<FuneralHomeInfo>({
        name: settings?.funeralHomeInfo?.name || '',
        taxId: settings?.funeralHomeInfo?.taxId || '',
        address: settings?.funeralHomeInfo?.address || '',
        phone: settings?.funeralHomeInfo?.phone || '',
        email: settings?.funeralHomeInfo?.email || currentUser?.email || '',
        website: settings?.funeralHomeInfo?.website || '',
        logo: settings?.funeralHomeInfo?.logo || ''
    });

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                const base64 = await fileToBase64Compressed(file, 400, 400, 0.8);
                setFormData({ ...formData, logo: base64 });
            } catch (error) {
                console.error("Error al subir imagen:", error);
                alert("Error al procesar el logo.");
            }
        }
    };

    const handleSaveProfile = async () => {
        if (!formData.name || !formData.taxId || !formData.phone) {
            alert("Por favor, rellena al menos el Nombre, NIF/CIF y Teléfono para los documentos legales.");
            return;
        }
        setIsProcessing(true);
        try {
            const targetFuneralHomeId = currentUser?.funeralHomeId;
            if (!targetFuneralHomeId) throw new Error("Sin Identificador de Funeraria.");
            
            await updateFirestoreSettings(formData, targetFuneralHomeId);
            await updateOnboardingStep('profile');
            setStep(2);
        } catch (e:any) {
            console.error(e);
            alert("Error guardando el perfil: " + e.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleInjectCatalog = async () => {
        setIsProcessing(true);
        try {
            const basicServices: IndividualService[] = [
                { id: `s-${Date.now()}-1`, name: "Féretro Ecológico Roble", description: "Ataúd estándar de madera noble ecológica.", price: 750 },
                { id: `s-${Date.now()}-2`, name: "Coche Fúnebre Superior", description: "Traslado en vehículo Mercedes Benz carrozado.", price: 250 },
                { id: `s-${Date.now()}-3`, name: "Servicio de Velatorio 24h", description: "Uso de sala VIP durante 24 horas con catering básico.", price: 600 }
            ];
            const basicPlan: ServicePlan = {
                id: `p-${Date.now()}`,
                name: "Plan Básico Despedida",
                description: "Paquete estándar y económico para servicios completos.",
                includedServices: basicServices.map(s => s.id),
                price: 1500
            };

            for (const s of basicServices) { await saveService(s); }
            await savePlan(basicPlan);

            await updateOnboardingStep('catalog');
            setStep(3);
        } catch (e:any) {
            console.error(e);
            alert("Error inyectando catálogo: " + e.message);
        } finally {
            setIsProcessing(false);
        }
    };
    
    const handleSkipCatalog = async () => {
        setIsProcessing(true);
        await updateOnboardingStep('catalog');
        setStep(3);
        setIsProcessing(false);
    };

    const handleInjectTemplates = async () => {
        setIsProcessing(true);
        try {
            const targetFuneralHomeId = currentUser?.funeralHomeId;
            if (!targetFuneralHomeId) throw new Error("Sin Identificador de Funeraria.");

            const template1: DocumentTemplate = {
                id: `t-${Date.now()}-1`,
                name: "Autorización de Traslado",
                content: JSON.stringify([
                    { id: '1', type: 'heading', content: { text: "Autorización de Traslado", level: 1 } },
                    { id: '2', type: 'paragraph', content: { text: "Por la presente se autoriza el servicio por {{company.name}}." } }
                ])
            };

            const template2: DocumentTemplate = {
                id: `t-${Date.now()}-2`,
                name: "Contrato de Servicios Funerarios",
                content: JSON.stringify([
                    { id: '1', type: 'heading', content: { text: "Contrato de Prestación", level: 1 } },
                    { id: '2', type: 'paragraph', content: { text: "Contratación por {{applicant.name}} por importe de {{service.totalPrice}}€." } }
                ])
            };

            await addFirestoreTemplate(template1, targetFuneralHomeId);
            await addFirestoreTemplate(template2, targetFuneralHomeId);

            // Fetch to ensure cache reflects it
            await fetchAllData();
            await updateOnboardingStep('templates');
            // Al actualizar step templates a true, app.tsx detectara completado y retirará el wizard.
        } catch (e:any) {
             console.error(e);
             alert("Error inyectando plantillas: " + e.message);
        } finally {
             setIsProcessing(false);
        }
    };

    const handleSkipTemplates = async () => {
        setIsProcessing(true);
        await updateOnboardingStep('templates');
        setIsProcessing(false);
    };

    return (
        <div className="fixed inset-0 z-[200] bg-slate-900/40 backdrop-blur-md flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-[40px] shadow-floating flex flex-col md:flex-row overflow-hidden animate-luxury-pop">
                
                {/* Panel Izquierdo: Progreso */}
                <div className="w-full md:w-1/3 bg-slate-50 dark:bg-slate-950 p-10 flex flex-col justify-between border-r border-slate-100 dark:border-slate-800">
                    <div>
                        <div className="flex items-center gap-3 mb-12">
                            <div className="h-8 w-8 bg-primary-500 rounded-xl flex items-center justify-center shadow-lg">
                                <span className="text-slate-900 font-extrabold text-lg">K</span>
                            </div>
                            <h1 className="text-xl font-bold tracking-widest uppercase text-slate-800 dark:text-white">KHARON</h1>
                        </div>
                        
                        <div className="space-y-8 relative before:absolute before:inset-y-0 before:left-6 before:ml-px before:w-0.5 before:bg-slate-200 dark:before:bg-slate-800">
                            
                            {[
                                { num: 1, title: 'Perfil', icon: <BuildingOfficeIcon className="w-5 h-5"/>, active: step === 1, completed: step > 1 },
                                { num: 2, title: 'Inventario', icon: <BriefcaseIcon className="w-5 h-5"/>, active: step === 2, completed: step > 2 },
                                { num: 3, title: 'Documentación', icon: <DocumentTextIcon className="w-5 h-5"/>, active: step === 3, completed: step > 3 }
                            ].map((s) => (
                                <div key={s.num} className={`relative flex items-center gap-6 ${!s.active && !s.completed ? 'opacity-40' : ''}`}>
                                    <div className={`relative z-10 w-12 h-12 flex items-center justify-center rounded-2xl shadow-sm transition-all duration-500 ${
                                        s.active ? 'bg-primary-500 text-slate-900 shadow-luxury scale-110' : 
                                        s.completed ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900' : 
                                        'bg-white dark:bg-slate-800 text-slate-400 border border-slate-200 dark:border-slate-700'
                                    }`}>
                                        {s.completed ? <CheckIcon className="w-6 h-6" /> : s.icon}
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-400">Paso {s.num}</p>
                                        <p className={`font-bold ${s.active ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-500'}`}>{s.title}</p>
                                    </div>
                                </div>
                            ))}

                        </div>
                    </div>

                    <div className="mt-12">
                         <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-2xl border border-primary-100 dark:border-primary-500/20">
                             <p className="text-xs font-medium text-primary-800 dark:text-primary-300">
                                <strong>Hola {currentUser?.name}.</strong> Vamos a dejar tu software operativo en menos de 2 minutos.
                             </p>
                         </div>
                    </div>
                </div>

                {/* Panel Derecho: Contenido Interactivo */}
                <div className="w-full md:w-2/3 p-10 lg:p-16 flex flex-col justify-center min-h-[600px] relative">
                    
                    {step === 1 && (
                        <div className="space-y-8 animate-fade-in">
                            <div>
                                <h2 className="text-3xl font-serif font-bold text-slate-900 dark:text-white mb-2">Configuremos tu Sede</h2>
                                <p className="text-slate-500 dark:text-slate-400">Los datos legales y tu logotipo aparecerán en todos los PDFs automáticos.</p>
                            </div>

                            <div className="flex items-center gap-8">
                                <div className="h-28 w-28 shrink-0 bg-slate-50 dark:bg-slate-800 rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center relative overflow-hidden group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                                    {formData.logo ? (
                                        <img src={formData.logo} className="w-full h-full object-contain p-2" alt="Logo" />
                                    ) : (
                                        <>
                                            <CameraIcon className="w-8 h-8 text-slate-300" />
                                            <span className="text-[10px] font-bold text-slate-400 mt-2 uppercase">Subir Logo</span>
                                        </>
                                    )}
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <UploadIcon className="w-6 h-6 text-white" />
                                    </div>
                                    <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                                </div>
                                
                                <div className="flex-1 space-y-4">
                                    <input 
                                        className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 focus:border-primary-500 outline-none font-bold"
                                        placeholder="Nombre Comercial / Razón Social *"
                                        value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                                    />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <input 
                                    className="px-5 py-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 focus:border-primary-500 outline-none font-bold text-sm"
                                    placeholder="NIF / CIF *"
                                    value={formData.taxId} onChange={e => setFormData({...formData, taxId: e.target.value})}
                                />
                                <input 
                                    className="px-5 py-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 focus:border-primary-500 outline-none font-bold text-sm"
                                    placeholder="Teléfono de Contacto *"
                                    value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})}
                                />
                                <input 
                                    className="col-span-2 px-5 py-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 focus:border-primary-500 outline-none font-bold text-sm"
                                    placeholder="Dirección Fiscal Completa"
                                    value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})}
                                />
                            </div>

                            <div className="pt-6">
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={isProcessing}
                                    className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold rounded-2xl shadow-luxury hover:opacity-90 active:scale-[0.98] transition-luxury disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-2"
                                >
                                    {isProcessing ? 'Guardando...' : 'Guardar y Continuar'}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-8 animate-fade-in flex flex-col h-full justify-center text-center">
                            <div className="inline-flex mx-auto p-4 bg-primary-50 dark:bg-primary-900/10 rounded-full text-primary-500 mb-6 shadow-soft">
                                <SparklesIcon className="w-12 h-12" />
                            </div>
                            <div>
                                <h2 className="text-3xl font-serif font-bold text-slate-900 dark:text-white mb-4">Añade Servicios con 1 Clic</h2>
                                <p className="text-slate-500 dark:text-slate-400 max-w-sm mx-auto leading-relaxed">
                                    En vez de crear tus productos a mano ahora, podemos **inyectar un catálogo básico** con Ataúdes, Traslados y Salas de Velatorio instantáneamente.
                                </p>
                            </div>

                            <div className="space-y-4 max-w-sm mx-auto w-full pt-8">
                                <button
                                    onClick={handleInjectCatalog}
                                    disabled={isProcessing}
                                    className="w-full py-4 bg-primary-500 text-slate-900 font-black tracking-wide rounded-2xl shadow-luxury hover:scale-[1.02] active:scale-[0.98] transition-luxury disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isProcessing ? 'Importando...' : 'Generar Catálogo Básico'}
                                </button>
                                <button
                                    onClick={handleSkipCatalog}
                                    disabled={isProcessing}
                                    className="w-full py-4 bg-transparent text-slate-400 font-bold hover:text-slate-600 dark:hover:text-white transition-colors"
                                >
                                    Saltar y hacerlo más tarde
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-8 animate-fade-in flex flex-col h-full justify-center text-center">
                            <div className="inline-flex mx-auto p-4 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-600 dark:text-white mb-6 shadow-soft">
                                <DocumentTextIcon className="w-12 h-12" />
                            </div>
                            <div>
                                <h2 className="text-3xl font-serif font-bold text-slate-900 dark:text-white mb-4">Plantillas de Documentos</h2>
                                <p className="text-slate-500 dark:text-slate-400 max-w-sm mx-auto leading-relaxed">
                                    Cargaremos plantillas legales de Autorización de Traslado y Contrato de Servicios con "Campos Inteligentes" integrados.
                                </p>
                            </div>

                            <div className="space-y-4 max-w-sm mx-auto w-full pt-8">
                                <button
                                    onClick={handleInjectTemplates}
                                    disabled={isProcessing}
                                    className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black tracking-wide rounded-2xl shadow-luxury hover:scale-[1.02] active:scale-[0.98] transition-luxury disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isProcessing ? 'Finalizando...' : 'Instalar y Finalizar Inicialización'}
                                </button>
                                <button
                                    onClick={handleSkipTemplates}
                                    disabled={isProcessing}
                                    className="w-full py-4 bg-transparent text-slate-400 font-bold hover:text-slate-600 dark:hover:text-white transition-colors"
                                >
                                    Saltar y empezar en blanco
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};
